import React, { useState } from 'react'
import { useFetch } from 'use-http'

import { Box, Button, Card, Heading, Text } from '@myxplor/stardust'

import sortObjectsBy from '~/src/utils/sortObjectsBy'

import { ServiceDetail } from './types'
import PlanChangeModal from './PlanChangeModal'

interface Props {
  service: ServiceDetail
  onRefreshServiceData(): void
}

const Plans = ({ service, onRefreshServiceData }: Props) => {
  const [createdPlans, setCreatedPlans] = useState<Papi.ServicePlan[]>([])
  const [isModalOpen, setModalOpen] = useState<boolean>(false)

  const featureSetRequest = useFetch(`/api/v1/feature_sets`, {}, [])

  const featureSets = (featureSetRequest.data?.data || []) as Papi.FeatureSet[]
  const featureSetLookup = featureSets.reduce((acc, featureSet) => {
    return { ...acc, [featureSet.id]: featureSet.name }
  }, {}) as Record<number, string>

  const plans = sortObjectsBy([...createdPlans, ...service.plans], 'id').reverse()

  const onPlanChange = async (plan: Papi.ServicePlan) => {
    setCreatedPlans((plans) => [plan, ...plans])
    setModalOpen(false)
    onRefreshServiceData()
  }

  return (
    <Card>
      <Box display="flex" flexDirection="column">
        <Box alignItems="center" display="flex" justifyContent="space-between" padding="small">
          <Heading level={5}>Plan History</Heading>
          <Button disabled={featureSetRequest.loading} onClick={() => setModalOpen(true)}>
            Change Plan
          </Button>
        </Box>
        {plans.length === 0 && (
          <Box borderColor="lowestEmphasis" border="1px solid" padding="small">
            <Text>No plan</Text>
          </Box>
        )}
        {plans.map((plan) => (
          <Box
            key={plan.id}
            borderColor="lowestEmphasis"
            border="1px solid"
            display="flex"
            padding="small"
            width={1}>
            <Box display="flex" flexDirection="column" flexGrow={1} paddingRight="large">
              <Text prefab="overline" weight="bold">
                Plan
              </Text>
              <Text lineHeight={1.4} prefab="body">
                {featureSetLookup[plan.feature_set_id]}
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" paddingRight="large" width="150px">
              <Text align="right" prefab="overline" weight="bold">
                Start date
              </Text>
              <Text align="right" lineHeight={1.4} prefab="body">
                {plan.start_date}
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" width="120px">
              <Text align="right" prefab="overline" weight="bold">
                End date
              </Text>
              <Text align="right" lineHeight={1.4} prefab="body">
                {plan.end_date || 'Ongoing'}
              </Text>
            </Box>
          </Box>
        ))}
      </Box>
      <PlanChangeModal
        featureSets={featureSets}
        isOpen={isModalOpen}
        service={service}
        onCancel={() => setModalOpen(false)}
        onSave={onPlanChange}
      />
    </Card>
  )
}

export default React.memo(Plans)
